import { PresenceAvatar, type Presence } from '../presence-avatar/presence-avatar';

/** Up to two initials of a person's name, for an avatar without a picture. */
export const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('');

interface PersonAvatarProps {
  name: string;
  /** Today's presence; leave out (or pass `off`) to show no ring and no dot. */
  presence?: Presence;
  /** The presence in words ("Clocked in · Since 09:05"), for the tooltip and screen readers. */
  presenceLabel?: string;
  size?: 24 | 32 | 40;
  className?: string;
}

/**
 * A person's avatar (prototype `av`): their initials with the presence ring and status dot People
 * shows. Use it wherever a person appears in a list, table or dialog, so everyone looks the same
 * everywhere; get `presence` from `usePersonPresence` in the directory module.
 */
export const PersonAvatar = ({
  name,
  presence = 'off',
  presenceLabel,
  size = 32,
  className,
}: PersonAvatarProps) => {
  const summary = presenceLabel ? `${name}, ${presenceLabel}` : name;
  return (
    <PresenceAvatar size={size} presence={presence} className={className}>
      <span role="img" aria-label={summary} title={summary}>
        {initialsOf(name)}
      </span>
    </PresenceAvatar>
  );
};
